import ApiClient from "./apiClient";
import {
  KubernetesVersion,
  KubernetesVersionDetail,
  Image,
  ImageCreate,
  ImageUpdate,
  Addon,
  AddonCreate,
  AddonRelease,
  AddonReleaseCreate,
  AddonReleaseDetail,
  AddonReleaseUpdate,
  Networking,
  NetworkingCreate,
  NetworkingUpdate,
  Operation,
  OperationDetail,
  Worker,
  WorkerDetail,
} from "../pages/settings/clusters/types";

interface ListParams {
  page?: number | null;
  page_size?: number | null;
  ordering?: string | null;
  search?: string | null;
  [key: string]: unknown;
}

const normalizeList = (res: { data: { results: unknown[]; [key: string]: unknown } }) => ({
  ...res,
  data: {
    ...res.data,
    objects: res.data.results,
  },
});

const buildParams = (params: ListParams) => {
  const { page, page_size, ordering, search, ...rest } = params;
  return {
    page,
    size: page_size,
    order_by: ordering,
    search,
    ...rest,
  };
};

const clusterSettingsService = {
  // Kubernetes Versions
  getVersions: (params: ListParams = {}) =>
    ApiClient.get('cluster-settings/kubernetes-versions', {
      params: buildParams(params),
    }).then(normalizeList),

  createVersion: (data: { id: string; visible?: boolean }) =>
    ApiClient.post('cluster-settings/kubernetes-versions', data),

  getVersion: (id: string) =>
    ApiClient.get<KubernetesVersionDetail>(`cluster-settings/kubernetes-versions/${id}`),

  updateVersion: (id: string, data: { visible: boolean }) =>
    ApiClient.put(`cluster-settings/kubernetes-versions/${id}`, data),

  deleteVersion: (id: string) =>
    ApiClient.delete(`cluster-settings/kubernetes-versions/${id}`),

  // Images
  getImages: (params: ListParams = {}) =>
    ApiClient.get('cluster-settings/images', {
      params: buildParams(params),
    }).then(normalizeList),

  createImage: (data: ImageCreate) =>
    ApiClient.post('cluster-settings/images', data),

  getImage: (id: number) =>
    ApiClient.get<Image>(`cluster-settings/images/${id}`),

  updateImage: (id: number, data: ImageUpdate) =>
    ApiClient.put(`cluster-settings/images/${id}`, data),

  deleteImage: (id: number) =>
    ApiClient.delete(`cluster-settings/images/${id}`),

  // Addons
  getAddons: (params: ListParams = {}) =>
    ApiClient.get('cluster-settings/addons', {
      params: buildParams(params),
    }).then(normalizeList),

  createAddon: (data: AddonCreate) =>
    ApiClient.post('cluster-settings/addons', data),

  getAddon: (id: number) =>
    ApiClient.get<Addon>(`cluster-settings/addons/${id}`),

  updateAddon: (id: number, data: AddonCreate) =>
    ApiClient.put(`cluster-settings/addons/${id}`, data),

  deleteAddon: (id: number) =>
    ApiClient.delete(`cluster-settings/addons/${id}`),

  getAddonTypes: () =>
    ApiClient.get<string[]>('cluster-settings/addons/types'),

  // Addon Releases
  getAddonReleases: (params: ListParams = {}) =>
    ApiClient.get('cluster-settings/addon-releases', {
      params: buildParams(params),
    }).then(normalizeList),

  createAddonRelease: (data: AddonReleaseCreate) =>
    ApiClient.post('cluster-settings/addon-releases', data),

  getAddonRelease: (id: number) =>
    ApiClient.get<AddonReleaseDetail>(`cluster-settings/addon-releases/${id}`),

  updateAddonRelease: (id: number, data: AddonReleaseUpdate) =>
    ApiClient.put(`cluster-settings/addon-releases/${id}`, data),

  deleteAddonRelease: (id: number) =>
    ApiClient.delete(`cluster-settings/addon-releases/${id}`),

  // Networkings
  getNetworkings: (params: ListParams = {}) =>
    ApiClient.get('cluster-settings/networkings', {
      params: buildParams(params),
    }).then(normalizeList),

  createNetworking: (data: NetworkingCreate) =>
    ApiClient.post('cluster-settings/networkings', data),

  getNetworking: (id: number) =>
    ApiClient.get<Networking>(`cluster-settings/networkings/${id}`),

  updateNetworking: (id: number, data: NetworkingUpdate) =>
    ApiClient.put(`cluster-settings/networkings/${id}`, data),

  deleteNetworking: (id: number) =>
    ApiClient.delete(`cluster-settings/networkings/${id}`),

  // Operations (NO POST — system-generated)
  getOperations: (params: ListParams = {}) =>
    ApiClient.get('cluster-settings/operations', {
      params: buildParams(params),
    }).then(normalizeList),

  getOperation: (id: string) =>
    ApiClient.get<OperationDetail>(`cluster-settings/operations/${id}`),

  updateOperation: (id: string, data: Record<string, unknown>) =>
    ApiClient.put(`cluster-settings/operations/${id}`, data),

  deleteOperation: (id: string) =>
    ApiClient.delete(`cluster-settings/operations/${id}`),

  // Workers
  getWorkers: (params: ListParams = {}) =>
    ApiClient.get('cluster-settings/workers', {
      params: buildParams(params),
    }).then(normalizeList),

  createWorker: (data: Record<string, unknown>) =>
    ApiClient.post('cluster-settings/workers', data),

  getWorker: (id: number) =>
    ApiClient.get<WorkerDetail>(`cluster-settings/workers/${id}`),

  updateWorker: (id: number, data: { status?: string; config_file?: string }) =>
    ApiClient.put(`cluster-settings/workers/${id}`, data),

  deleteWorker: (id: number) =>
    ApiClient.delete(`cluster-settings/workers/${id}`),

  getWorkerStatuses: () =>
    ApiClient.get<string[]>('cluster-settings/workers/statuses'),
};

export default clusterSettingsService;
